import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subject, takeUntil, filter, take, forkJoin } from 'rxjs';
import { ExternalDataService, ScrapCollectionInsights, ScrapItemInsight } from '../../services/externalDataService.service';
import { AuthService } from '../../services/auth.service';
import { AgentService } from '../../services/agent.service';
import { DistrictZoneFilterComponent } from '../shared/district-zone-filter/district-zone-filter.component';

@Component({
  selector: 'app-scrap-collection-dashboard',
  standalone: true,
  imports: [CommonModule, FormsModule, DistrictZoneFilterComponent],
  templateUrl: './scrap-collection-dashboard.component.html',
  styleUrl: './scrap-collection-dashboard.component.css'
})
export class ScrapCollectionDashboardComponent implements OnInit, OnDestroy {
  insights: ScrapCollectionInsights | null = null;
  loading = false;
  errorMessage = '';
  
  // Filters
  selectedAgents: string[] = [];
  selectedGeoZones: string[] = [];
  fromDate: string = '';
  toDate: string = '';
  
  // Dropdown data
  agents: { value: string; label: string }[] = [];

  // Dropdown states
  agentDropdownOpen = false;

  // UI State
  private destroy$ = new Subject<void>();
  private documentClickHandler: (event: Event) => void;
  showTopItems = 5;
  sortBy: 'totalValue' | 'netQuantity' | 'collectionCount' = 'totalValue';
  sortDirection: 'asc' | 'desc' = 'desc';

  constructor(
    private externalDataService: ExternalDataService,
    private authService: AuthService,
    private agentService: AgentService
  ) {
    this.documentClickHandler = this.handleDocumentClick.bind(this);
  }

  ngOnInit(): void {
    document.addEventListener('click', this.documentClickHandler);
    // Set default date range (last 30 days)
    const today = new Date();
    const thirtyDaysAgo = new Date(today);
    thirtyDaysAgo.setDate(today.getDate() - 30);
    
    this.toDate = this.formatDateForInput(today);
    this.fromDate = this.formatDateForInput(thirtyDaysAgo);
    
    // Wait for authentication to be ready before loading data
    this.authService.authReady$
      .pipe(
        filter(ready => ready === true),
        take(1),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.loadDropdownData();
        this.loadInsights();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    document.removeEventListener('click', this.documentClickHandler);
  }

  private handleDocumentClick(event: Event): void {
    const target = event.target as HTMLElement;

    // Agent dropdown
    const agentDropdown = document.querySelector('#agentFilter')?.closest('.status-dropdown-container');
    if (agentDropdown && !agentDropdown.contains(target)) {
      this.agentDropdownOpen = false;
    }
  }

  private formatDateForInput(date: Date): string {
    return date.toISOString().split('T')[0];
  }

  private formatDateForApi(dateString: string): string {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toISOString();
  }

  loadDropdownData(): void {
    this.agentService.getAgentsForSelection()
    .pipe(takeUntil(this.destroy$))
    .subscribe({
      next: (agents) => {
        this.agents = agents;
      },
      error: (error) => {
        console.error('Error loading dropdown data:', error);
      }
    });
  }

  loadInsights(): void {
    this.loading = true;
    this.errorMessage = '';
    
    const fromDateIso = this.fromDate ? this.formatDateForApi(this.fromDate) : undefined;
    const toDateIso = this.toDate ? this.formatDateForApi(this.toDate) : undefined;
    
    this.externalDataService.getScrapCollectionInsights(
      this.selectedAgents.length > 0 ? this.selectedAgents.join(',') : undefined,
      this.selectedGeoZones.length > 0 ? this.selectedGeoZones.join(',') : undefined,
      fromDateIso,
      toDateIso
    )
    .pipe(takeUntil(this.destroy$))
    .subscribe({
      next: (data) => {
        this.insights = data;
        this.loading = false;
      },
      error: (error) => {
        console.error('Error loading insights:', error);
        
        if (error.status === 401) {
          this.errorMessage = 'Authentication required. Please log in again.';
        } else if (error.status === 403) {
          this.errorMessage = 'Access denied. You do not have permission to view insights.';
        } else if (error.status === 404) {
          this.errorMessage = 'Insights data not found.';
        } else if (error.status >= 500) {
          this.errorMessage = 'Server error. Please try again later.';
        } else if (error.status === 0 || error.status === undefined) {
          this.errorMessage = 'Network error. Please check your connection and try again.';
        } else {
          this.errorMessage = 'Failed to load insights. Please try again.';
        }
        
        this.loading = false;
      }
    });
  }

  applyFilters(): void {
    this.loadInsights();
  }

  resetFilters(): void {
    this.selectedAgents = [];
    this.selectedGeoZones = [];
    
    // Reset to last 30 days
    const today = new Date();
    const thirtyDaysAgo = new Date(today);
    thirtyDaysAgo.setDate(today.getDate() - 30);
    
    this.toDate = this.formatDateForInput(today);
    this.fromDate = this.formatDateForInput(thirtyDaysAgo);
    
    this.loadInsights();
  }

  toggleAgent(agentId: string): void {
    const index = this.selectedAgents.indexOf(agentId);
    if (index > -1) {
      this.selectedAgents.splice(index, 1);
    } else {
      this.selectedAgents.push(agentId);
    }
  }

  isAgentSelected(agentId: string): boolean {
    return this.selectedAgents.includes(agentId);
  }

  getAgentDisplayText(): string {
    if (this.selectedAgents.length === 0) {
      return 'All Agents';
    }
    if (this.selectedAgents.length === 1) {
      return (
        this.agents.find((a) => a.value === this.selectedAgents[0])
          ?.label || 'Selected'
      );
    }
    return `${this.selectedAgents.length} selected`;
  }

  toggleAgentDropdown(): void {
    this.agentDropdownOpen = !this.agentDropdownOpen;
  }

  closeAgentDropdown(): void {
    this.agentDropdownOpen = false;
  }

  getSortedScrapItems(): ScrapItemInsight[] {
    if (!this.insights || !this.insights.scrapItemInsights) {
      return [];
    }
    
    const items = [...this.insights.scrapItemInsights];
    
    items.sort((a, b) => {
      let aValue: number;
      let bValue: number;
      
      switch (this.sortBy) {
        case 'totalValue':
          aValue = a.totalValue;
          bValue = b.totalValue;
          break;
        case 'netQuantity':
          aValue = a.netQuantity;
          bValue = b.netQuantity;
          break;
        case 'collectionCount':
          aValue = a.collectionCount;
          bValue = b.collectionCount;
          break;
      }
      
      if (this.sortDirection === 'desc') {
        return bValue - aValue;
      } else {
        return aValue - bValue;
      }
    });
    
    return items;
  }

  getTopScrapItems(): ScrapItemInsight[] {
    return this.getSortedScrapItems().slice(0, this.showTopItems);
  }

  changeSortBy(sortBy: 'totalValue' | 'netQuantity' | 'collectionCount'): void {
    if (this.sortBy === sortBy) {
      // Toggle direction if same sort
      this.sortDirection = this.sortDirection === 'desc' ? 'asc' : 'desc';
    } else {
      this.sortBy = sortBy;
      this.sortDirection = 'desc';
    }
  }

  formatCurrency(value: number): string {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(value);
  }

  formatNumber(value: number): string {
    return new Intl.NumberFormat('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  }

  getPercentageOfTotal(value: number): number {
    if (!this.insights || this.insights.totalScrapValue === 0) {
      return 0;
    }
    return (value / this.insights.totalScrapValue) * 100;
  }

  exportToCSV(): void {
    if (!this.insights || !this.insights.scrapItemInsights) {
      return;
    }

    const headers = [
      'Scrap Item Name',
      'Unit',
      'Total Quantity',
      'Deducted Quantity',
      'Net Quantity',
      'Total Value (₹)',
      'Collection Count'
    ];

    const rows = this.getSortedScrapItems().map(item => [
      item.scrapItemName,
      item.unit,
      item.totalQuantity.toString(),
      item.totalDeductedQuantity.toString(),
      item.netQuantity.toString(),
      item.totalValue.toFixed(2),
      item.collectionCount.toString()
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', `scrap-collection-insights-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
}

